/**
 * Store Service
 * Handles all HTTP requests related to store operations
 * Pure service layer - no business logic, just HTTP calls
 */

import { buildDashboardLink } from "@/helpers/urlHelper";

/**
 * Resolves store information from its alias. The dashboard for a store
 * always lives at https://{alias}.biyo.co, so this is computed locally
 * rather than looked up from a directory service.
 * @param {string} storeAlias - The store alias identifier
 * @returns {Promise<Response>} Fetch-like response
 */
export async function fetchStoreInfo(storeAlias) {
  return {
    ok: true,
    status: 200,
    json: async () => ({
      alias: storeAlias,
      dashboard_link: buildDashboardLink(storeAlias)
    })
  };
}

/**
 * Fetches store locations (branches/sucursales) list from dashboard
 * @param {string} dashboardLink - The dashboard base URL
 * @returns {Promise<Response>} Raw fetch response
 */
export async function fetchStoreLocations(dashboardLink) {
  const url = `${dashboardLink}/api/orderlive/stores/`;
  return fetch(url);
}

/**
 * Fetches store configuration by store ID
 * @param {string} dashboardLink - The dashboard base URL
 * @param {number|string} storeId - The store ID
 * @returns {Promise<Response>} Raw fetch response
 */
export async function fetchStoreConfig(dashboardLink, storeId) {
  const url = `${dashboardLink}/api/orderlive/config/${storeId}/`;
  return fetch(url);
}

/**
 * Fetches active promotion rules from dashboard.
 * @param {string} dashboardLink - The dashboard base URL
 * @returns {Promise<Response>} Raw fetch response
 */
export async function fetchPromotions(dashboardLink) {
  const cleanBaseUrl = dashboardLink.replace(/\/+$/, "");
  const url = `${cleanBaseUrl}/api/promotions?channel=online_order`;
  return fetch(url);
}
